import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { userApi, dataApi } from '../api/client'
import type { UserProfile, UserStats, MeshData, StreamingStats } from '../types'
import { LEVEL_META, LEVEL_QUOTA } from '../constants/plan'
import type { UserLevel } from '../constants/plan'

interface Props {
  open: boolean
  onClose: () => void
}

type Tab = 'profile' | 'token' | 'data' | 'stats'

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  PENDING:    { label: '대기중',   color: 'text-gray-400' },
  CONVERTING: { label: '변환중',   color: 'text-yellow-400' },
  SUCCESS:    { label: '완료',     color: 'text-green-400' },
  FAILED:     { label: '실패',     color: 'text-red-400' },
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit',
  })
}

function formatShortBytes(bytes: number): string {
  if (bytes === 0) return '0'
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}K`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)}M`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)}G`
}

// ISO 주 문자열 반환 (예: "2026-W21")
function getISOWeekStr(date: Date): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const day = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - day)
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`
}

// "2026-W21" → 해당 주 월요일/일요일 날짜 문자열
function isoWeekToDate(weekStr: string, endOfWeek = false): string {
  const [y, w] = weekStr.split('-W').map(Number)
  const jan4 = new Date(y, 0, 4)
  const dayOfWeek = (jan4.getDay() + 6) % 7
  const monday = new Date(jan4.getTime() - dayOfWeek * 86400000 + (w - 1) * 7 * 86400000)
  if (endOfWeek) monday.setDate(monday.getDate() + 6)
  return monday.toISOString().split('T')[0]
}

// "2026-05" → 해당 월의 마지막 날
function isoMonthLastDay(monthStr: string): string {
  const [y, m] = monthStr.split('-').map(Number)
  return new Date(y, m, 0).toISOString().split('T')[0]
}

// 기간별 라벨 포맷 (스트리밍 차트용)
function formatPeriodLabel(date: string, period: 'daily' | 'weekly' | 'monthly'): string {
  if (period === 'monthly') {
    const [, m] = date.split('-')
    return `${parseInt(m)}월`
  }
  if (period === 'weekly') return date.split('-')[1]
  const parts = date.split('-')
  return `${parseInt(parts[1])}/${parseInt(parts[2])}`
}

// 업로드 추이: dataItems를 기간+범위로 집계
function computeUploadStats(
  items: MeshData[],
  period: 'daily' | 'weekly' | 'monthly',
  from: string,
  to: string,
): Array<{ label: string; count: number }> {
  if (!from || !to || !items.length) return []
  if (period === 'weekly' && (!from.includes('-W') || !to.includes('-W'))) return []
  if (period === 'monthly' && (from.length !== 7 || to.length !== 7)) return []
  if (period === 'daily' && (from.length !== 10 || to.length !== 10)) return []

  let fromDate: Date, toDate: Date
  if (period === 'weekly') {
    fromDate = new Date(isoWeekToDate(from))
    toDate = new Date(isoWeekToDate(to, true))
  } else if (period === 'monthly') {
    fromDate = new Date(`${from}-01`)
    toDate = new Date(isoMonthLastDay(to))
  } else {
    fromDate = new Date(from)
    toDate = new Date(to)
  }
  toDate.setHours(23, 59, 59)

  // 전체 기간의 슬롯 생성
  const slots: string[] = []
  if (period === 'daily') {
    const d = new Date(fromDate)
    while (d <= toDate) {
      slots.push(d.toISOString().split('T')[0])
      d.setDate(d.getDate() + 1)
    }
  } else if (period === 'weekly') {
    let cur = new Date(fromDate)
    while (cur <= toDate) {
      const k = getISOWeekStr(cur)
      if (!slots.includes(k)) slots.push(k)
      cur.setDate(cur.getDate() + 7)
    }
    // to 주도 포함
    const toKey = getISOWeekStr(toDate)
    if (!slots.includes(toKey)) slots.push(toKey)
  } else {
    const [fy, fm] = from.split('-').map(Number)
    const [ty, tm] = to.split('-').map(Number)
    let y = fy, m = fm
    while (y < ty || (y === ty && m <= tm)) {
      slots.push(`${y}-${String(m).padStart(2, '0')}`)
      m++; if (m > 12) { m = 1; y++ }
    }
  }

  const counts = new Map<string, number>(slots.map(s => [s, 0]))

  items.forEach(item => {
    const d = new Date(item.createdAt)
    if (d < fromDate || d > toDate) return
    let key: string
    if (period === 'daily') {
      key = item.createdAt.split('T')[0]
    } else if (period === 'weekly') {
      key = getISOWeekStr(d)
    } else {
      key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    }
    counts.set(key, (counts.get(key) ?? 0) + 1)
  })

  return slots.map(key => {
    let label: string
    if (period === 'daily') {
      const p = key.split('-'); label = `${parseInt(p[1])}/${parseInt(p[2])}`
    } else if (period === 'weekly') {
      label = key.split('-')[1]
    } else {
      label = `${parseInt(key.split('-')[1])}월`
    }
    return { label, count: counts.get(key) ?? 0 }
  })
}

function UploadLineChart({ data }: { data: { label: string; value: number }[] }) {
  if (data.length === 0) return null
  const W = 560, H = 100
  const PAD = { top: 8, right: 8, bottom: 24, left: 32 }
  const iW = W - PAD.left - PAD.right
  const iH = H - PAD.top - PAD.bottom
  const maxVal = Math.max(...data.map(d => d.value), 1)
  const xPos = (i: number) => PAD.left + (data.length > 1 ? i / (data.length - 1) : 0.5) * iW
  const yPos = (v: number) => PAD.top + iH - (v / maxVal) * iH
  const linePoints = data.map((d, i) => `${xPos(i)},${yPos(d.value)}`).join(' ')
  const areaPoints = `${xPos(0)},${PAD.top + iH} ${linePoints} ${xPos(data.length - 1)},${PAD.top + iH}`
  const yTicks = [0, 0.5, 1].map(r => Math.round(maxVal * r))
  const xStep = Math.max(1, Math.ceil(data.length / 8))
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 110 }}>
      {yTicks.map((tick, i) => (
        <g key={i}>
          <line x1={PAD.left} y1={yPos(tick)} x2={PAD.left + iW} y2={yPos(tick)}
            stroke="#334155" strokeWidth="0.5" strokeDasharray={tick === 0 ? undefined : '3,3'} />
          <text x={PAD.left - 4} y={yPos(tick) + 3.5} textAnchor="end" fontSize="8" fill="#64748b">
            {tick}
          </text>
        </g>
      ))}
      {data.length > 1 && <polygon points={areaPoints} fill="rgba(59,130,246,0.12)" />}
      {data.length > 1 && (
        <polyline points={linePoints} fill="none" stroke="#3b82f6" strokeWidth="1.5" strokeLinejoin="round" />
      )}
      {data.map((d, i) => d.value > 0 && (
        <circle key={i} cx={xPos(i)} cy={yPos(d.value)} r="2.5" fill="#3b82f6" />
      ))}
      {data.map((d, i) => (i % xStep === 0 || i === data.length - 1) && (
        <text key={i} x={xPos(i)} y={H - 4} textAnchor="middle" fontSize="8" fill="#64748b">
          {d.label}
        </text>
      ))}
    </svg>
  )
}

function LineChart({ data }: { data: { label: string; value: number }[] }) {
  if (data.length === 0) return null
  const W = 560, H = 100
  const PAD = { top: 8, right: 8, bottom: 24, left: 48 }
  const iW = W - PAD.left - PAD.right
  const iH = H - PAD.top - PAD.bottom
  const maxVal = Math.max(...data.map(d => d.value), 1)
  const xPos = (i: number) => PAD.left + (data.length > 1 ? i / (data.length - 1) : 0.5) * iW
  const yPos = (v: number) => PAD.top + iH - (v / maxVal) * iH
  const linePoints = data.map((d, i) => `${xPos(i)},${yPos(d.value)}`).join(' ')
  const areaPoints = `${xPos(0)},${PAD.top + iH} ${linePoints} ${xPos(data.length - 1)},${PAD.top + iH}`
  const yTicks = [0, 0.5, 1].map(r => maxVal * r)
  const xStep = Math.max(1, Math.ceil(data.length / 8))
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 110 }}>
      {yTicks.map((tick, i) => (
        <g key={i}>
          <line x1={PAD.left} y1={yPos(tick)} x2={PAD.left + iW} y2={yPos(tick)}
            stroke="#334155" strokeWidth="0.5" strokeDasharray={tick === 0 ? undefined : '3,3'} />
          <text x={PAD.left - 4} y={yPos(tick) + 3.5} textAnchor="end" fontSize="8" fill="#64748b">
            {formatShortBytes(tick)}
          </text>
        </g>
      ))}
      {data.length > 1 && <polygon points={areaPoints} fill="rgba(59,130,246,0.12)" />}
      {data.length > 1 && (
        <polyline points={linePoints} fill="none" stroke="#3b82f6" strokeWidth="1.5" strokeLinejoin="round" />
      )}
      {data.map((d, i) => d.value > 0 && (
        <circle key={i} cx={xPos(i)} cy={yPos(d.value)} r="2.5" fill="#3b82f6" />
      ))}
      {data.map((d, i) => (i % xStep === 0 || i === data.length - 1) && (
        <text key={i} x={xPos(i)} y={H - 4} textAnchor="middle" fontSize="8" fill="#64748b">
          {d.label}
        </text>
      ))}
    </svg>
  )
}

function PeriodSelector({
  value, onChange,
}: { value: 'daily' | 'weekly' | 'monthly'; onChange: (v: 'daily' | 'weekly' | 'monthly') => void }) {
  return (
    <div className="flex gap-1">
      {(['daily', 'weekly', 'monthly'] as const).map((p) => (
        <button key={p} onClick={() => onChange(p)}
          className={`text-[11px] px-2 py-0.5 rounded transition-colors ${
            value === p ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white border border-slate-600'
          }`}>
          {p === 'daily' ? '일별' : p === 'weekly' ? '주별' : '월별'}
        </button>
      ))}
    </div>
  )
}

function DateRangePicker({
  period, from, to, onFromChange, onToChange, maxDate, maxWeek, maxMonth,
}: {
  period: 'daily' | 'weekly' | 'monthly'
  from: string; to: string
  onFromChange: (v: string) => void; onToChange: (v: string) => void
  maxDate: string; maxWeek: string; maxMonth: string
}) {
  const type = period === 'daily' ? 'date' : period === 'weekly' ? 'week' : 'month'
  const maxVal = period === 'daily' ? maxDate : period === 'weekly' ? maxWeek : maxMonth
  const cls = 'text-[10px] bg-slate-800 border border-slate-600 rounded px-1.5 py-0.5 text-slate-300 focus:outline-none focus:border-blue-400'
  return (
    <div className="flex items-center gap-1 mt-1">
      <input type={type} value={from} max={maxVal} onChange={e => onFromChange(e.target.value)} className={cls} />
      <span className="text-slate-500 text-[10px]">~</span>
      <input type={type} value={to} max={maxVal} onChange={e => onToChange(e.target.value)} className={cls} />
    </div>
  )
}

// 기간별 기본 날짜 범위 계산
function defaultRange(period: 'daily' | 'weekly' | 'monthly') {
  const today = new Date()
  if (period === 'daily') {
    const from = new Date(today); from.setDate(from.getDate() - 13)
    return { from: from.toISOString().split('T')[0], to: today.toISOString().split('T')[0] }
  }
  if (period === 'weekly') {
    const from = new Date(today); from.setDate(from.getDate() - 7 * 7)
    return { from: getISOWeekStr(from), to: getISOWeekStr(today) }
  }
  const from = new Date(today); from.setMonth(from.getMonth() - 11)
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    from: `${from.getFullYear()}-${pad(from.getMonth() + 1)}`,
    to: `${today.getFullYear()}-${pad(today.getMonth() + 1)}`,
  }
}

export default function UserInfoModal({ open, onClose }: Props) {
  const { token } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>('profile')
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [stats, setStats] = useState<UserStats | null>(null)
  const [dataItems, setDataItems] = useState<MeshData[]>([])
  const [loading, setLoading] = useState(false)
  const [tokenVisible, setTokenVisible] = useState(false)
  const [copied, setCopied] = useState(false)

  // 스트리밍 사용량 상태
  const [streamingPeriod, setStreamingPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily')
  const [streamingFrom, setStreamingFrom] = useState('')
  const [streamingTo, setStreamingTo] = useState('')
  const [streamingDataId, setStreamingDataId] = useState<number | null>(null)
  const [streamingStats, setStreamingStats] = useState<StreamingStats | null>(null)
  const [streamingLoading, setStreamingLoading] = useState(false)

  // 업로드 추이 상태
  const [statPeriod, setStatPeriod] = useState<'daily' | 'weekly' | 'monthly'>('monthly')
  const [uploadFrom, setUploadFrom] = useState('')
  const [uploadTo, setUploadTo] = useState('')

  // 오늘 날짜 (input max 속성용)
  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], [])
  const todayWeekStr = useMemo(() => getISOWeekStr(new Date()), [])
  const todayMonthStr = useMemo(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  }, [])

  // 스트리밍 기간 변경 시 날짜 초기화
  useEffect(() => {
    const r = defaultRange(streamingPeriod)
    setStreamingFrom(r.from)
    setStreamingTo(r.to)
  }, [streamingPeriod])

  // 업로드 추이 기간 변경 시 날짜 초기화
  useEffect(() => {
    const r = defaultRange(statPeriod)
    setUploadFrom(r.from)
    setUploadTo(r.to)
  }, [statPeriod])

  const fetchAll = useCallback(async () => {
    setLoading(true)
    const [p, s, d] = await Promise.allSettled([
      userApi.getProfile(),
      userApi.getStats(),
      dataApi.listAll({ size: 100 }),
    ])
    if (p.status === 'fulfilled') setProfile(p.value)
    if (s.status === 'fulfilled') setStats(s.value)
    if (d.status === 'fulfilled') setDataItems(d.value.content.filter((item) => !item.sample))
    setLoading(false)
  }, [])

  const fetchStreamingStats = useCallback(async () => {
    if (!streamingFrom || !streamingTo) return
    if (streamingPeriod === 'weekly' && (!streamingFrom.includes('-W') || !streamingTo.includes('-W'))) return
    if (streamingPeriod === 'monthly' && (streamingFrom.length !== 7 || streamingTo.length !== 7)) return
    if (streamingPeriod === 'daily' && (streamingFrom.length !== 10 || streamingTo.length !== 10)) return
    setStreamingLoading(true)
    try {
      let apiFrom: string, apiTo: string
      if (streamingPeriod === 'weekly') {
        apiFrom = isoWeekToDate(streamingFrom)
        apiTo = isoWeekToDate(streamingTo, true)
      } else if (streamingPeriod === 'monthly') {
        apiFrom = `${streamingFrom}-01`
        apiTo = isoMonthLastDay(streamingTo)
      } else {
        apiFrom = streamingFrom
        apiTo = streamingTo
      }
      const result = await userApi.getStreamingStats({
        period: streamingPeriod,
        dataId: streamingDataId ?? undefined,
        from: apiFrom,
        to: apiTo,
      })
      setStreamingStats(result)
    } catch {
      // ignore
    } finally {
      setStreamingLoading(false)
    }
  }, [streamingPeriod, streamingDataId, streamingFrom, streamingTo])

  useEffect(() => {
    if (open) {
      setActiveTab('profile')
      setTokenVisible(false)
      fetchAll()
    }
  }, [open, fetchAll])

  useEffect(() => {
    if (open && activeTab === 'stats' && streamingFrom && streamingTo) {
      fetchStreamingStats()
    }
  }, [open, activeTab, fetchStreamingStats])

  const handleCopy = async () => {
    if (!token) return
    await navigator.clipboard.writeText(token)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // 원본 파일 크기 합계 (dataItems에서 계산 — DB 값이 0인 경우 보완)
  const totalOriginalSizeComputed = useMemo(
    () => dataItems.reduce((sum, item) => sum + (item.originalFileSize ?? 0), 0),
    [dataItems]
  )

  // 데이터별 타일 크기 맵 (stats.dataSizes에서 구성)
  const tileSizeMap = useMemo(
    () => new Map((stats?.dataSizes ?? []).map(d => [d.id, d.tileSize])),
    [stats]
  )

  // 업로드 추이 집계 (dataItems 기반, 프론트엔드 계산)
  const uploadStats = useMemo(
    () => computeUploadStats(dataItems, statPeriod, uploadFrom, uploadTo),
    [dataItems, statPeriod, uploadFrom, uploadTo]
  )
  if (!open) return null

  const tabs: { key: Tab; label: string }[] = [
    { key: 'profile', label: '기본 정보' },
    { key: 'token',   label: 'Access Token' },
    { key: 'data',    label: '데이터 목록' },
    { key: 'stats',   label: '사용현황' },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-[740px] max-h-[88vh] bg-slate-800 border border-slate-700 rounded-xl shadow-2xl flex flex-col">

        {/* 헤더 */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700 shrink-0">
          <h2 className="text-white font-semibold text-sm">내 정보</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* 탭 */}
        <div className="flex border-b border-slate-700 shrink-0">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`px-5 py-2.5 text-xs font-medium transition-colors ${
                activeTab === t.key
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-slate-400 hover:text-white'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* 컨텐츠 */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="flex items-center justify-center h-40 text-slate-400 text-sm">불러오는 중...</div>
          )}

          {/* ── 기본 정보 ── */}
          {!loading && activeTab === 'profile' && (
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-slate-600 flex items-center justify-center shrink-0">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-slate-300">
                    <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                  </svg>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-white font-semibold">{profile?.name ?? '-'}</p>
                    {profile?.userLevel && (() => {
                      const meta = LEVEL_META[profile.userLevel as UserLevel]
                      return meta ? (
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.bg} ${meta.color}`}>
                          {meta.label}
                        </span>
                      ) : null
                    })()}
                  </div>
                  <p className="text-slate-400 text-xs mt-0.5">{profile?.email ?? '-'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: '아이디', value: profile?.userId },
                  { label: '이름', value: profile?.name },
                  { label: '이메일', value: profile?.email },
                  { label: '소속', value: profile?.organization || '미입력' },
                  { label: '로그인 방식', value: profile?.loginType === 'NAVER' ? '네이버' : '이메일' },
                  { label: '가입일', value: profile?.createdAt ? formatDate(profile.createdAt) : '-' },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-slate-700/50 rounded-lg px-4 py-3">
                    <p className="text-slate-400 text-[11px] mb-1">{label}</p>
                    <p className="text-white text-xs font-medium">{value ?? '-'}</p>
                  </div>
                ))}
              </div>

              {/* 요금제 / 쿼터 */}
              {profile?.userLevel && (() => {
                const quota = LEVEL_QUOTA[profile.userLevel as UserLevel]
                const meta  = LEVEL_META[profile.userLevel as UserLevel]
                if (!quota || !meta) return null
                return (
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-slate-300 text-xs font-medium">요금제 · 쿼터</p>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.bg} ${meta.color}`}>
                        {meta.label} — {quota.monthlyFee}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { label: '변환 (GiB/월)', value: `${quota.conversionGib} GiB` },
                        { label: '저장 (GiB)', value: `${quota.storageGib} GiB` },
                        { label: '스트리밍 (GiB/월)', value: `${quota.streamingGib} GiB` },
                        { label: '다운로드', value: quota.download },
                      ].map(({ label, value }) => (
                        <div key={label} className="bg-slate-800/60 rounded px-3 py-2">
                          <p className="text-slate-500 text-[10px]">{label}</p>
                          <p className="text-white text-xs font-semibold mt-0.5">{value}</p>
                        </div>
                      ))}
                    </div>

                    {/* 등급 조정 링크 */}
                    <div className="mt-3 pt-3 border-t border-slate-600/50 flex justify-end">
                      <button
                        onClick={() => { onClose(); navigate('/plan') }}
                        className="text-[11px] text-blue-400 hover:text-blue-300 border border-blue-700 hover:border-blue-500 rounded px-3 py-1 transition-colors"
                      >
                        등급 조정 →
                      </button>
                    </div>
                  </div>
                )
              })()}
            </div>
          )}

          {/* ── Access Token ── */}
          {!loading && activeTab === 'token' && (
            <div className="space-y-4">
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-300 text-xs font-medium">API Access Token</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setTokenVisible((v) => !v)}
                      className="text-[11px] text-slate-400 hover:text-white border border-slate-600 rounded px-2 py-0.5 transition-colors">
                      {tokenVisible ? '숨기기' : '보기'}
                    </button>
                    <button onClick={handleCopy}
                      className={`text-[11px] border rounded px-2 py-0.5 transition-colors ${
                        copied ? 'text-green-400 border-green-600' : 'text-slate-400 hover:text-white border-slate-600'
                      }`}>
                      {copied ? '복사됨!' : '복사'}
                    </button>
                  </div>
                </div>
                <div className="bg-slate-900 rounded p-3 font-mono text-[11px] break-all text-slate-300 min-h-[48px]">
                  {token
                    ? tokenVisible ? token : token.slice(0, 20) + '••••••••••••••••••••••••' + token.slice(-10)
                    : '토큰 없음'}
                </div>
              </div>
              <div className="text-slate-400 text-[11px] space-y-1 bg-slate-700/30 rounded-lg p-4">
                <p className="text-slate-300 font-medium mb-2">토큰 사용 방법</p>
                <p>API 요청 시 HTTP 헤더에 포함하세요:</p>
                <code className="block bg-slate-900 rounded p-2 mt-1 text-[10px] text-slate-300">
                  Authorization: Bearer {'<token>'}
                </code>
                <p className="mt-2">토큰 갱신은 재로그인을 통해 진행할 수 있습니다.</p>
              </div>
            </div>
          )}

          {/* ── 데이터 목록 ── */}
          {!loading && activeTab === 'data' && (
            <div>
              {dataItems.length === 0 ? (
                <div className="text-center text-slate-400 text-sm py-16">업로드된 데이터가 없습니다.</div>
              ) : (
                <div className="space-y-2">
                  <p className="text-slate-400 text-xs mb-3">총 {dataItems.length}개의 데이터</p>
                  {/* 헤더 */}
                  <div className="grid text-[10px] text-slate-500 px-3 pb-1" style={{ gridTemplateColumns: '1fr 80px 80px 80px 48px' }}>
                    <span>이름 / 파일명</span>
                    <span className="text-right">원본 크기</span>
                    <span className="text-right">타일 크기</span>
                    <span className="text-right">생성일</span>
                    <span className="text-right">상태</span>
                  </div>
                  {dataItems.map((item) => {
                    const st = STATUS_LABEL[item.status] ?? { label: item.status, color: 'text-slate-400' }
                    const tileSize = tileSizeMap.get(item.id)
                    return (
                      <div key={item.id} className="bg-slate-700/50 rounded-lg px-3 py-2.5 grid gap-x-2 items-center"
                        style={{ gridTemplateColumns: '1fr 80px 80px 80px 48px' }}>
                        <div className="min-w-0">
                          <p className="text-white text-xs font-medium truncate">{item.name}</p>
                          <p className="text-slate-500 text-[10px] truncate">{item.originalFileName}</p>
                        </div>
                        <span className="text-slate-400 text-[11px] text-right">
                          {item.originalFileSize != null && item.originalFileSize > 0
                            ? formatBytes(item.originalFileSize) : '-'}
                        </span>
                        <span className="text-slate-400 text-[11px] text-right">
                          {tileSize != null && tileSize > 0 ? formatBytes(tileSize) : '-'}
                        </span>
                        <span className="text-slate-500 text-[11px] text-right">
                          {item.createdAt ? formatDate(item.createdAt) : '-'}
                        </span>
                        <span className={`text-[11px] font-medium text-right ${st.color}`}>{st.label}</span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── 사용현황 ── */}
          {!loading && activeTab === 'stats' && stats && (
            <div className="space-y-5">

              {/* 요약 카드 */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: '전체 데이터', value: stats.totalCount, color: 'text-white' },
                  { label: '변환 완료', value: stats.successCount, color: 'text-green-400' },
                  { label: '변환 실패', value: stats.failedCount, color: 'text-red-400' },
                  { label: '변환 중', value: stats.convertingCount, color: 'text-yellow-400' },
                  { label: '대기 중', value: stats.pendingCount, color: 'text-gray-400' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-slate-700/50 rounded-lg px-4 py-3">
                    <p className="text-slate-400 text-[11px]">{label}</p>
                    <p className={`text-xl font-bold mt-1 ${color}`}>{value}</p>
                  </div>
                ))}
              </div>

              {/* 스토리지 사용량 */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-slate-300 text-xs font-medium mb-3">스토리지 사용량</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-slate-400 text-[11px]">원본 파일 합계</p>
                    <p className="text-white text-sm font-semibold mt-1">
                      {formatBytes(totalOriginalSizeComputed || stats.totalOriginalSize)}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-[11px]">3D Tiles 합계</p>
                    <p className="text-white text-sm font-semibold mt-1">
                      {formatBytes((stats.dataSizes ?? []).reduce((s, d) => s + d.tileSize, 0))}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-[11px]">Object Storage 전체</p>
                    <p className="text-white text-sm font-semibold mt-1">{formatBytes(stats.storageSize)}</p>
                  </div>
                </div>
              </div>

              {/* Data Streaming 사용량 */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3 gap-2 flex-wrap">
                  <div>
                    <p className="text-slate-300 text-xs font-medium">Data Streaming</p>
                    {streamingStats && (
                      <p className="text-white text-lg font-bold mt-0.5">{formatBytes(streamingStats.totalBytes)}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <PeriodSelector value={streamingPeriod} onChange={setStreamingPeriod} />
                      <select
                        value={streamingDataId ?? ''}
                        onChange={e => setStreamingDataId(e.target.value === '' ? null : Number(e.target.value))}
                        className="text-[11px] bg-slate-800 border border-slate-600 rounded px-2 py-0.5 text-slate-300 focus:outline-none focus:border-blue-400">
                        <option value="">전체</option>
                        {dataItems.map(item => (
                          <option key={item.id} value={item.id}>{item.name}</option>
                        ))}
                      </select>
                    </div>
                    <DateRangePicker
                      period={streamingPeriod}
                      from={streamingFrom} to={streamingTo}
                      onFromChange={setStreamingFrom} onToChange={setStreamingTo}
                      maxDate={todayStr} maxWeek={todayWeekStr} maxMonth={todayMonthStr}
                    />
                  </div>
                </div>
                {streamingLoading ? (
                  <div className="flex items-center justify-center h-24 text-slate-400 text-xs">불러오는 중...</div>
                ) : streamingStats && streamingStats.dailyStats.some(s => s.bytes > 0) ? (
                  <LineChart data={streamingStats.dailyStats.map(s => ({
                    label: formatPeriodLabel(s.date, streamingPeriod),
                    value: s.bytes,
                  }))} />
                ) : (
                  <p className="text-slate-400 text-xs text-center py-6">사용 데이터 없음</p>
                )}
              </div>

              {/* 업로드 추이 */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3 gap-2 flex-wrap">
                  <div>
                    <p className="text-slate-300 text-xs font-medium">업로드 추이</p>
                    {uploadStats.length > 0 && (
                      <p className="text-white text-lg font-bold mt-0.5">
                        {uploadStats.reduce((s, d) => s + d.count, 0)}건
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <PeriodSelector value={statPeriod} onChange={setStatPeriod} />
                    <DateRangePicker
                      period={statPeriod}
                      from={uploadFrom} to={uploadTo}
                      onFromChange={setUploadFrom} onToChange={setUploadTo}
                      maxDate={todayStr} maxWeek={todayWeekStr} maxMonth={todayMonthStr}
                    />
                  </div>
                </div>
                {uploadStats.length === 0 ? (
                  <p className="text-slate-400 text-xs text-center py-6">데이터 없음</p>
                ) : (
                  <UploadLineChart data={uploadStats.map(s => ({ label: s.label, value: s.count }))} />
                )}
              </div>

              {/* 3D Tiles API 네트워크 사용량 */}
              <div className="bg-slate-700/50 rounded-lg px-4 py-3 flex items-center justify-between">
                <p className="text-slate-400 text-[11px]">3D Tiles API 누적 네트워크 사용량</p>
                <p className="text-white text-sm font-semibold">{formatBytes(stats.tileNetworkUsage)}</p>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  )
}
